import { useEffect, useRef, useState } from 'react';
import { useReducedMotion } from 'framer-motion';

/**
 * Which row of a focused list is open, and what moves when that changes.
 *
 * The pillars and Discovery both show one question at a time, close it a beat
 * after it is answered, open the next and bring it to the middle of the screen.
 * That rhythm is the design (Feature 110, option 2c); this is the part of it
 * that is timing and focus rather than markup, so the two screens cannot drift
 * on it the way two copies of a filter once did.
 *
 * What it does NOT decide is when a question counts as answered. A readiness
 * question is answered by its one tap; a Discovery multi-select is answered when
 * the client says so. The caller schedules the advance.
 */

/**
 * How long a just-answered question stays open before it closes.
 *
 * The handoff's value. It was briefly raised, because at 300ms alone the screen
 * looked like it had changed by itself and the answer had not registered — but
 * that was before the row animated and before every row kept a numbered chip.
 * Those two carry the feedback now, so the wait does not have to, and holding
 * the row open longer only reads as lag.
 */
export const ADVANCE_MS = 300;

type FocusedList = {
  /** The open row, or null when every row is answered. */
  activeIndex: number | null;
  /** Attach to the open row; focus and scrolling are moved here. */
  rowRef: React.RefObject<HTMLDivElement | null>;
  /** Attach to the step's Continue button; focus lands here when nothing is open. */
  continueRef: React.RefObject<HTMLButtonElement | null>;
  /** The client asked to come back to a row they can see. Opens it, moves nothing else. */
  reopen: (position: number) => void;
  /** A row was answered. `next` is where to go, computed from the answer just made. */
  advanceTo: (from: number, next: number | null) => void;
};

export function useFocusedList({
  firstUnanswered,
  resetKey,
}: {
  /** The first row with no answer, or null when the list is complete. */
  firstUnanswered: () => number | null;
  /** Changing it reopens at the first unanswered row — a new pillar is a new page. */
  resetKey?: string;
}): FocusedList {
  // Honoured in JS as the rest of the flow honours it in CSS via motion-reduce.
  const reduceMotion = useReducedMotion();

  // No fallback to 0: a step returned to with every answer saved opens nothing,
  // the same as one just completed.
  const [activeIndex, setActiveIndex] = useState<number | null>(() => firstUnanswered());

  const advanceTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const rowRef = useRef<HTMLDivElement>(null);
  const continueRef = useRef<HTMLButtonElement>(null);

  /*
   * Focus follows the client's own action only. Moving it on any other re-render
   * would yank a keyboard user out of wherever they had gone.
   *
   * Advancing and reopening both move focus to the row; only advancing scrolls,
   * and both take the scroll away from `focus()` so it cannot add one of its
   * own. Reopening is a click on a row the client is already looking at, so
   * pulling the page under them would be movement they did not ask for.
   */
  const moveFocus = useRef<'advance' | 'reopen' | 'continue' | null>(null);

  useEffect(() => {
    if (resetKey === undefined) return;
    // The previous step's pending advance has to go with it. Answering the last
    // question enables Continue, and a save that resolves inside the advance
    // window would let that timer land on the new step and close the question it
    // had just opened.
    clearTimeout(advanceTimer.current ?? undefined);
    moveFocus.current = null;
    setActiveIndex(firstUnanswered());
    // Re-seeding on every answer would fight the advance; the step key is the
    // only thing that should reset this.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [resetKey]);

  useEffect(() => () => clearTimeout(advanceTimer.current ?? undefined), []);

  useEffect(() => {
    const intent = moveFocus.current;
    moveFocus.current = null;

    // Also given up here, not only on the advance. `focus()` scrolls by
    // default, so leaving it meant the page could still jump on a click the
    // client made on a row they were already looking at — which is the one
    // thing this branch exists to avoid.
    if (intent === 'reopen' && activeIndex !== null) {
      rowRef.current?.focus({ preventScroll: true });
    }

    if (intent === 'advance' && activeIndex !== null) {
      /*
       * `focus()` scrolls by itself — instantly, and only far enough to clear
       * the viewport edge. That is the opposite of what is wanted here and it
       * would fight the centred scroll below, so the focus call gives up its
       * scroll and this owns it.
       *
       * Centring is approximate on purpose: the row is still expanding from 0fr
       * to 1fr as this runs, and the row above it is collapsing, so the exact
       * centre is a moving target. Chasing it with a second timer would buy a
       * few pixels and another thing to cancel on unmount.
       */
      rowRef.current?.focus({ preventScroll: true });
      rowRef.current?.scrollIntoView({
        block: 'center',
        behavior: reduceMotion ? 'auto' : 'smooth',
      });
    }

    // Nothing is open once the last question is answered, and the control that
    // was just pressed is now disabled inside a collapsed panel — so focus goes
    // to the step's next action rather than being dropped on the body.
    if (intent === 'continue') continueRef.current?.focus();
  }, [activeIndex, reduceMotion]);

  const advanceTo = (from: number, next: number | null) => {
    clearTimeout(advanceTimer.current ?? undefined);
    advanceTimer.current = setTimeout(() => {
      moveFocus.current = next === null ? 'continue' : next === from ? null : 'advance';
      setActiveIndex(next);
    }, ADVANCE_MS);
  };

  /*
   * Reopening is the client's own instruction, so it wins over a pending
   * advance. Without cancelling, a Change clicked inside the advance window
   * opens that row and then loses it a moment later — to a row the engine chose,
   * with focus following.
   *
   * Focus follows here too, and has to: the element just activated is the
   * collapsed header, and opening the row turns that header from a button into a
   * plain div. Leaving focus where it was dropped it on the body, so a keyboard
   * user pressed Enter on Change and then had to tab from the top of the
   * document to reach the options they had asked for.
   */
  const reopen = (position: number) => {
    clearTimeout(advanceTimer.current ?? undefined);
    moveFocus.current = 'reopen';
    setActiveIndex(position);
  };

  return { activeIndex, rowRef, continueRef, reopen, advanceTo };
}
